import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';

import VendorTrucksAssignedSummary from '~components/TruckCapacity/VendorTrucksAssignedSummary';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { getDriverInfoForCards } from '~utils/drivers/driver-utils';

import { OverflowAwareText } from '../../Order/ordersDispatchStyledComponents';
import { SelectedAssignment } from './hooks/useAssignDriverMenu';

interface DriverMenuItemProps {
  entity: SelectedAssignment['entity'];
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled?: boolean;
  filterDate?: string;
}

const DriverMenuItem: React.FC<DriverMenuItemProps> = ({
  entity,
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = false,
  filterDate,
}) => {
  const theme = useTheme();
  const menuItemSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '4px',
    padding: '8px',
    '&:hover': { bgcolor: theme.brandV2.colors.treadGray8 },
    borderRadius: theme.brandV2.borderRadius,
    maxWidth: '320px',
  };

  if (entity instanceof AccountCapacityTypeahead) {
    return (
      <Box sx={menuItemSx}>
        <VendorTrucksAssignedSummary vendor={entity} />
      </Box>
    );
  }

  const info = getDriverInfoForCards(
    entity,
    entity.companyShare?.senderCompany,
    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
  );

  return (
    <Box sx={menuItemSx}>
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            justifyContent: 'space-between',
          }}
        >
          <OverflowAwareText
            variant="body2"
            sx={{
              color: info.isAvailable
                ? theme.brandV2.colors.treadBlue2
                : theme.brandV2.colors.treadBlack,
              cursor: 'auto',
              fontWeight: 600,
              width: 'auto',
            }}
          >
            {info.equipmentName && (
              <>
                <Typography
                  component="span"
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                  }}
                >
                  {info.equipmentName}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                  }}
                >
                  {' • '}
                </Typography>
              </>
            )}

            <Typography
              component="span"
              sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
            >
              {info.fullName}
            </Typography>
          </OverflowAwareText>

          {filterDate && Boolean(entity.companyDayDriverStat?.jobsCount) && (
            <Chip
              label={entity.companyDayDriverStat?.jobsCount}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '11px',
                bgcolor: theme.brandV2.colors.treadGray1,
                color: theme.semantic.textInverse,
                height: 16,
              }}
            />
          )}
        </Box>

        {info.truckType && (
          <OverflowAwareText
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
          >
            {info.truckType}
          </OverflowAwareText>
        )}

        {info.vendorName && (
          <OverflowAwareText
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
          >
            {info.vendorName}
          </OverflowAwareText>
        )}
      </Box>
    </Box>
  );
};

export { DriverMenuItem };
